/**
 * Группы механизаторов и средние ставки маш.-часа по объектам — логика без
 * React (ТЗ AI-Economist-2026 ред. 2.1, п. 6.3). Подписи единиц и баз, доля
 * простоя в процентах, ключ должности Хронума, форма группы ↔ тело запроса,
 * строки таблицы ставок на дату, проверка даты «действует с», ПРЦ модели на
 * дату.
 *
 * Деньги, коэффициенты и доли не проходят через float: сравнение и сдвиг
 * запятой — на строках. Значения импортируются только из `./model` — тест
 * (`groups.test.mjs`) подключает его своим resolve-хуком.
 */
import type {
  FleetGroupData,
  FleetGroupDetail,
  FleetGroupPosition,
  FleetGroupUnit,
  FleetModelPrc,
  GroupRate,
  GroupRateBasis,
  GroupRateEffective,
  GroupRateKind,
  GroupRatePick,
} from '@shared/api';
import { NBSP, formatDecimal, normalizeDecimal, typo } from './model';

/** WORD JOINER: «₽/маш.-ч» не рвётся на косой черте и дефисе. */
const WJ = String.fromCharCode(0x2060);

// ── Числа-строки ───────────────────────────────────────────────────────────

const DECIMAL = /^(-?)(\d+)(?:\.(\d+))?$/;

interface DecimalParts {
  neg: boolean;
  int: string;
  frac: string;
}

/** «012.500» → целая «12», дробная «5»; минус у нуля не считается. */
function decimalParts(raw: string): DecimalParts | null {
  const m = DECIMAL.exec(raw.trim());
  if (!m) return null;
  const int = m[2].replace(/^0+(?=\d)/, '');
  const frac = (m[3] ?? '').replace(/0+$/, '');
  const zero = int === '0' && frac === '';
  return { neg: m[1] === '-' && !zero, int, frac };
}

/** Сравнение чисел-строк с точкой без float: -1, 0 или 1; не число — NaN. */
export function compareDecimal(a: string, b: string): number {
  const x = decimalParts(a);
  const y = decimalParts(b);
  if (!x || !y) return Number.NaN;
  if (x.neg !== y.neg) return x.neg ? -1 : 1;
  const sign = x.neg ? -1 : 1;
  if (x.int.length !== y.int.length) return sign * (x.int.length < y.int.length ? -1 : 1);
  if (x.int !== y.int) return sign * (x.int < y.int ? -1 : 1);
  const len = Math.max(x.frac.length, y.frac.length);
  const fx = x.frac.padEnd(len, '0');
  const fy = y.frac.padEnd(len, '0');
  if (fx === fy) return 0;
  return sign * (fx < fy ? -1 : 1);
}

/** Знаков после запятой без хвостовых нулей: «12.50» → 1. */
export function fractionDigits(raw: string): number {
  return decimalParts(raw)?.frac.length ?? 0;
}

/**
 * Сдвиг запятой на строке: `shiftDecimal('0.3', 2)` → «30» (доля → проценты),
 * `shiftDecimal('12.5', -2)` → «0.125» (проценты → доля). Не число → `null`.
 */
export function shiftDecimal(raw: string, places: number): string | null {
  const p = decimalParts(raw);
  if (!p) return null;
  let digits = p.int + p.frac;
  let point = p.int.length + places;
  if (point <= 0) {
    digits = '0'.repeat(1 - point) + digits;
    point = 1;
  }
  if (point > digits.length) digits = digits.padEnd(point, '0');
  const int = digits.slice(0, point).replace(/^0+(?=\d)/, '');
  const frac = digits.slice(point).replace(/0+$/, '');
  return `${p.neg ? '-' : ''}${int}${frac ? `.${frac}` : ''}`;
}

// ── Подписи ────────────────────────────────────────────────────────────────

export const GROUP_UNITS: readonly FleetGroupUnit[] = ['machine_hour', 'day'];

export function isGroupUnit(value: string): value is FleetGroupUnit {
  return value === 'machine_hour' || value === 'day';
}

const UNIT_LABEL: Readonly<Record<FleetGroupUnit, string>> = {
  machine_hour: `маш.-${WJ}ч`,
  day: `сутки на${NBSP}объекте`,
};

/** Единица учёта группы: «маш.-ч» или «сутки на объекте». */
export function groupUnitLabel(unit: string): string {
  return isGroupUnit(unit) ? UNIT_LABEL[unit] : unit;
}

/** Пояснение единицы учёта — под переключателем в форме. */
export const GROUP_UNIT_HINT: Readonly<Record<FleetGroupUnit, string>> = {
  machine_hour: 'Часы механизаторов из Хронума, переведённые в маш.-часы.',
  day: 'Дни на объекте — для техники без своего механизатора: прицепы, вагоны-дома, опалубка.',
};

/** Единица ставки группы: «₽/маш.-ч» или «₽/сутки», неразрывно. */
export function rateUnit(unit: string): string {
  return unit === 'day' ? `₽${WJ}/${WJ}сутки` : `₽${WJ}/${WJ}маш.-${WJ}ч`;
}

export function isRateBasis(value: string): value is GroupRateBasis {
  return value === 'cost' || value === 'prc';
}

export const BASIS_LABEL: Readonly<Record<GroupRateBasis, string>> = {
  cost: 'Себестоимость',
  prc: 'ПРЦ',
};

/** Что значит база — одной строкой под переключателем. */
export const BASIS_HINT: Readonly<Record<GroupRateBasis, string>> = {
  cost: 'Производственная себестоимость маш.-часа без прибыли — как в январском БДР.',
  prc: 'Планово-расчётная цена: себестоимость с рентабельностью.',
};

/** База в строке таблицы: «себестоимость», «ПРЦ». */
export function basisText(basis: string | null | undefined): string {
  if (basis === 'cost') return 'себестоимость';
  if (basis === 'prc') return 'ПРЦ';
  return basis ?? '—';
}

export const RATE_KIND_LABEL: Readonly<Record<GroupRateKind, string>> = {
  project: 'ставка объекта',
  default: 'по умолчанию',
  missing: 'нет ставки',
};

export const PICK_LABEL: Readonly<Record<GroupRatePick, string>> = {
  average: 'Среднее',
  weighted: 'Средневзвешенное',
};

// ── Коэффициент и доля простоя ─────────────────────────────────────────────

/** Коэффициент: «1.0000» → «1», «1.2500» → «1,25». */
export function formatCoef(raw: string | null | undefined): string {
  if (!raw) return '—';
  return formatDecimal(raw, { minFraction: 0, maxFraction: 4, grouping: true }) ?? raw;
}

/** Доля ставки за простой: «0.3000» → «30 %». */
export function formatShare(raw: string | null | undefined): string {
  if (!raw) return '—';
  const pct = shiftDecimal(raw, 2);
  const text =
    pct === null ? null : formatDecimal(pct, { minFraction: 0, maxFraction: 2, grouping: true });
  return text === null ? raw : `${text}${NBSP}%`;
}

// ── Должности Хронума ──────────────────────────────────────────────────────

/**
 * Ключ должности Хронума из названия (п. 9.2 ТЗ): нижний регистр, «ё» → «е»,
 * буквы и цифры через одиночный пробел. По ключу склеиваются одинаковые
 * должности разных юрлиц.
 */
export function chronumPositionKey(name: string): string {
  return name
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

// ── Форма группы ───────────────────────────────────────────────────────────

export interface GroupFormValues {
  name: string;
  unit: FleetGroupUnit;
  /** Маш.-ч на час механизатора, как ввёл человек: «1,25». */
  hoursCoef: string;
  /** Доля ставки за простой в процентах: «30». */
  downtimePercent: string;
  /** Должность механизатора паспорта — по ней подбор находит модели. */
  operatorPosition: string;
  comment: string;
}

export type GroupFieldKey = keyof GroupFormValues;

/** Должность Хронума в форме: строка списка с местным id. */
export interface PositionDraft {
  rowId: string;
  name: string;
  key: string;
  /** Ключ правили руками — из названия он больше не подставляется. */
  keyEdited: boolean;
}

export interface GroupDraft {
  values: GroupFormValues;
  positions: PositionDraft[];
  /** Состав группы целиком, в порядке показа. */
  modelIds: string[];
}

export const GROUP_FIELD_LABEL: Readonly<Record<GroupFieldKey, string>> = {
  name: 'Название группы',
  unit: 'Единица учёта',
  hoursCoef: 'Маш.-ч на час механизатора',
  downtimePercent: 'Доля ставки за простой',
  operatorPosition: 'Должность механизатора в паспорте',
  comment: 'Комментарий',
};

const GROUP_FIELDS: readonly GroupFieldKey[] = [
  'name',
  'unit',
  'hoursCoef',
  'downtimePercent',
  'operatorPosition',
  'comment',
];

/** По умолчанию — как в январском БДР: маш.-ч = час механизатора, простой — 30 %. */
export const DEFAULT_HOURS_COEF = '1';
export const DEFAULT_DOWNTIME_PERCENT = '30';

let rowSeq = 0;

/** Новый местный id строки должности. */
export function positionRowId(): string {
  rowSeq += 1;
  return `pos-${rowSeq}`;
}

/** Пустая строка должности для «Добавить должность». */
export function emptyPosition(): PositionDraft {
  return { rowId: positionRowId(), name: '', key: '', keyEdited: false };
}

/** Строка-число API в поле ввода: «1.2500» → «1,25». */
function decimalInput(raw: string, maxFraction: number): string {
  return formatDecimal(raw, { minFraction: 0, maxFraction, grouping: false }) ?? raw;
}

/** Черновик формы из карточки группы; `null` — новая группа. */
export function groupDraft(detail: FleetGroupDetail | null): GroupDraft {
  const g = detail?.group;
  return {
    values: {
      name: g?.name ?? '',
      unit: g?.unit ?? 'machine_hour',
      hoursCoef: g ? decimalInput(g.hours_coef, 4) : DEFAULT_HOURS_COEF,
      downtimePercent: g
        ? decimalInput(shiftDecimal(g.downtime_share, 2) ?? g.downtime_share, 2)
        : DEFAULT_DOWNTIME_PERCENT,
      operatorPosition: g?.operator_position ?? '',
      comment: g?.comment ?? '',
    },
    // Ключи с сервера — уже чьё-то решение: при правке названия не трогаем.
    positions: (g?.positions ?? []).map((p) => ({
      rowId: positionRowId(),
      name: p.name,
      key: p.key,
      keyEdited: true,
    })),
    modelIds: detail?.models.map((m) => m.model_id) ?? [],
  };
}

/** Новое название строки должности: ключ следует за ним, пока его не правили. */
export function renamePosition(row: PositionDraft, name: string): PositionDraft {
  return row.keyEdited ? { ...row, name } : { ...row, name, key: chronumPositionKey(name) };
}

/**
 * Должности, которые уйдут на сервер: без пустых строк, ключ — в виде
 * Хронума (сервер нормализует его так же, `osnova_core::chronum::position_key`).
 */
function filledPositions(rows: readonly PositionDraft[]): FleetGroupPosition[] {
  return rows
    .filter((r) => r.key.trim() !== '' || r.name.trim() !== '')
    .map((r) => ({ key: chronumPositionKey(r.key), name: r.name.trim() }));
}

/** Список должностей без учёта порядка — чтобы понять, меняли ли его. */
function positionsSignature(rows: readonly PositionDraft[]): string {
  return JSON.stringify(
    filledPositions(rows)
      .map((p) => JSON.stringify([p.key, p.name]))
      .sort(),
  );
}

export interface GroupChanges {
  fields: GroupFieldKey[];
  positions: boolean;
  added: string[];
  removed: string[];
}

/** Что изменил человек относительно открытой версии. */
export function groupChanges(draft: GroupDraft, initial: GroupDraft): GroupChanges {
  const before = new Set(initial.modelIds);
  const after = new Set(draft.modelIds);
  return {
    fields: GROUP_FIELDS.filter((k) => draft.values[k] !== initial.values[k]),
    positions: positionsSignature(draft.positions) !== positionsSignature(initial.positions),
    added: draft.modelIds.filter((id) => !before.has(id)),
    removed: initial.modelIds.filter((id) => !after.has(id)),
  };
}

/** Сколько правок: поля, список должностей целиком, каждая добавленная и убранная модель. */
export function changeCount(c: GroupChanges): number {
  return c.fields.length + (c.positions ? 1 : 0) + c.added.length + c.removed.length;
}

export interface GroupIssue {
  /** Поле формы или строка должности: `position:<rowId>:name|key`. */
  key: string;
  message: string;
}

export function positionIssueKey(rowId: string, part: 'name' | 'key'): string {
  return `position:${rowId}:${part}`;
}

/** Коэффициент для API или ошибка. */
function coefValue(input: string): { value: string } | { error: string } {
  const n = normalizeDecimal(input);
  if (n === null) return { error: 'Нужно число, например 1 или 1,25' };
  if (compareDecimal(n, '0.0001') < 0 || compareDecimal(n, '100') > 0) {
    return { error: 'От 0,0001 до 100' };
  }
  if (fractionDigits(n) > 4) return { error: 'Не больше четырёх знаков после запятой' };
  return { value: n };
}

/** Доля простоя для API из процентов или ошибка. */
function shareValue(input: string): { value: string } | { error: string } {
  const n = normalizeDecimal(input);
  if (n === null) return { error: 'Нужно число процентов от 0 до 100' };
  if (compareDecimal(n, '0') < 0 || compareDecimal(n, '100') > 0) {
    return { error: 'От 0 до 100 %' };
  }
  if (fractionDigits(n) > 2) return { error: 'Не больше двух знаков после запятой' };
  const share = shiftDecimal(n, -2);
  return share === null ? { error: 'Нужно число процентов от 0 до 100' } : { value: share };
}

/**
 * Тело `POST`/`PUT /fleet/groups` и ошибки проверки. Новой группе уходят все
 * поля; при правке — только изменённые: нетронутое поле не перезапишет
 * значение, которое в нём сейчас на сервере.
 */
export function groupPayload(
  draft: GroupDraft,
  initial: GroupDraft,
  isNew: boolean,
): { data: FleetGroupData; issues: GroupIssue[] } {
  const issues: GroupIssue[] = [];
  const v = draft.values;
  const changes = groupChanges(draft, initial);
  const changed = (k: GroupFieldKey) => isNew || changes.fields.includes(k);
  const data: FleetGroupData = {};

  const name = v.name.trim();
  if (name === '') issues.push({ key: 'name', message: 'Укажите название группы' });
  else if (changed('name')) data.name = name;

  if (changed('unit')) data.unit = v.unit;

  const coef = coefValue(v.hoursCoef);
  if ('error' in coef) issues.push({ key: 'hoursCoef', message: coef.error });
  else if (changed('hoursCoef')) data.hours_coef = coef.value;

  const share = shareValue(v.downtimePercent);
  if ('error' in share) issues.push({ key: 'downtimePercent', message: share.error });
  else if (changed('downtimePercent')) data.downtime_share = share.value;

  // Пустая строка очищает поле — `null` backend понял бы как «не менять».
  const position = v.operatorPosition.trim();
  if (changed('operatorPosition') && (!isNew || position !== '')) {
    data.operator_position = position;
  }
  const comment = v.comment.trim();
  if (changed('comment') && (!isNew || comment !== '')) data.comment = comment;

  // Ключи сравниваем в виде Хронума: «Машинист Бульдозера» и «машинист
  // бульдозера» — одна должность.
  const seen = new Map<string, string>();
  for (const row of draft.positions) {
    const rowName = row.name.trim();
    const rawKey = row.key.trim();
    const rowKey = chronumPositionKey(rawKey);
    if (rowName === '' && rawKey === '') continue;
    if (rowName === '') {
      issues.push({
        key: positionIssueKey(row.rowId, 'name'),
        message: 'Укажите название должности',
      });
    }
    if (rowKey === '') {
      issues.push({
        key: positionIssueKey(row.rowId, 'key'),
        message: rawKey === '' ? 'Укажите ключ должности' : 'В ключе нужны буквы или цифры',
      });
    } else if (seen.has(rowKey)) {
      issues.push({
        key: positionIssueKey(row.rowId, 'key'),
        message: typo(`Такой ключ уже есть в списке — у должности «${seen.get(rowKey)}»`),
      });
    } else {
      seen.set(rowKey, rowName || rowKey);
    }
  }
  if (isNew ? filledPositions(draft.positions).length > 0 : changes.positions) {
    data.positions = filledPositions(draft.positions);
  }
  if (isNew ? draft.modelIds.length > 0 : changes.added.length + changes.removed.length > 0) {
    data.model_ids = [...draft.modelIds];
  }
  return { data, issues };
}

export interface GroupRebase {
  draft: GroupDraft;
  initial: GroupDraft;
  /** Поля, которые меняли и человек, и тот, кто сохранил раньше: значение коллеги. */
  conflicts: Partial<Record<GroupFieldKey, string>>;
  /** Список должностей правили оба — сохранится список человека. */
  positionsConflict: boolean;
}

/**
 * Перечитать группу после 409: свежая версия становится основой, правки
 * человека переносятся поверх. Модели — разницей: добавленные остаются
 * добавленными, убранные — убранными, остальной состав берётся свежий.
 */
export function rebaseGroupDraft(
  draft: GroupDraft,
  initial: GroupDraft,
  fresh: FleetGroupDetail,
): GroupRebase {
  const base = groupDraft(fresh);
  const changes = groupChanges(draft, initial);
  const values: GroupFormValues = { ...base.values };
  const conflicts: Partial<Record<GroupFieldKey, string>> = {};
  for (const k of changes.fields) {
    if (k === 'unit') values.unit = draft.values.unit;
    else values[k] = draft.values[k];
    if (base.values[k] !== initial.values[k]) conflicts[k] = base.values[k];
  }
  const removed = new Set(changes.removed);
  const modelIds = base.modelIds.filter((id) => !removed.has(id));
  for (const id of changes.added) if (!modelIds.includes(id)) modelIds.push(id);
  return {
    draft: {
      values,
      positions: changes.positions ? draft.positions : base.positions,
      modelIds,
    },
    initial: base,
    conflicts,
    positionsConflict:
      changes.positions &&
      positionsSignature(base.positions) !== positionsSignature(initial.positions),
  };
}

// ── Даты ───────────────────────────────────────────────────────────────────

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `YYYY-MM-DD` и такой день есть в календаре. */
export function isIsoDate(value: string): boolean {
  const m = ISO_DATE.exec(value);
  if (!m) return false;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.toISOString().slice(0, 10) === value;
}

/** Сегодня по часам пользователя, `YYYY-MM-DD`. */
export function todayIso(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** День ± n: календарная арифметика в UTC, без часовых поясов. */
export function addDaysIso(iso: string, days: number): string {
  const m = ISO_DATE.exec(iso);
  if (!m) return iso;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days));
  return d.toISOString().slice(0, 10);
}

/** «2026-11-01» → «01.11.2026». */
export function formatIsoDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const m = ISO_DATE.exec(iso.slice(0, 10));
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

/** «с 01.11.2026» или «с 01.07.2026 по 31.10.2026». */
export function periodText(from: string, to: string | null): string {
  const start = `с${NBSP}${formatIsoDate(from)}`;
  return to ? `${start} по${NBSP}${formatIsoDate(to)}` : start;
}

// ── Ставки ─────────────────────────────────────────────────────────────────

export interface RateValue {
  rate: string;
  validFrom: string | null;
  basis: GroupRateBasis | null;
}

/** Ставка группы на дату: объекта, по умолчанию для холдинга или нет. */
export type RateState =
  ({ kind: 'project' } & RateValue) | ({ kind: 'default' } & RateValue) | { kind: 'missing' };

export interface RateRow {
  groupId: string;
  groupName: string;
  unit: FleetGroupUnit;
  state: RateState;
  /** Ставка по умолчанию на ту же дату — видна и при своей ставке объекта. */
  fallback: RateValue | null;
}

/**
 * Строки таблицы из `GET /group-rates/effective`: с объектом — своя ставка
 * объекта, иначе по умолчанию, иначе нет; без объекта — только ставки по
 * умолчанию для холдинга.
 */
export function effectiveRateRows(items: readonly GroupRateEffective[]): RateRow[] {
  return items.map((r) => {
    const fallback: RateValue | null =
      r.default_rate !== null
        ? { rate: r.default_rate, validFrom: r.default_valid_from, basis: r.default_basis }
        : null;
    const state: RateState =
      r.project_rate !== null
        ? {
            kind: 'project',
            rate: r.project_rate,
            validFrom: r.project_valid_from,
            basis: r.project_basis,
          }
        : fallback
          ? { kind: 'default', ...fallback }
          : { kind: 'missing' };
    return { groupId: r.group_id, groupName: r.group_name, unit: r.unit, state, fallback };
  });
}

export function rateKindCounts(rows: readonly RateRow[]): Record<GroupRateKind, number> {
  const out: Record<GroupRateKind, number> = { project: 0, default: 0, missing: 0 };
  for (const r of rows) out[r.state.kind] += 1;
  return out;
}

/** Последняя ставка в истории — её закроет новая. */
export function openRate(history: readonly GroupRate[]): GroupRate | null {
  return history.find((r) => r.valid_to === null) ?? null;
}

/** «Действует с» для новой ставки: желаемая дата, но позже начала последней ставки. */
export function defaultValidFrom(preferred: string, open: GroupRate | null): string {
  if (!open || preferred > open.valid_from) return preferred;
  return addDaysIso(open.valid_from, 1);
}

/** Задним числом ставку не меняют: новая дата — строго позже начала последней. */
export function validFromIssue(validFrom: string, open: GroupRate | null): string | null {
  if (!isIsoDate(validFrom)) return 'Укажите дату, с которой действует ставка';
  if (open && validFrom <= open.valid_from) {
    return typo(
      `Последняя ставка действует с ${formatIsoDate(open.valid_from)}. Новая должна начинаться позже: задним числом ставку не меняют.`,
    );
  }
  return null;
}

export const RATE_MAX = '100000000';

/** Ставка вручную: строка-число с точкой для API или ошибка. */
export function parseRateInput(input: string): { value: string } | { error: string } {
  if (input.trim() === '') return { error: 'Укажите ставку' };
  const n = normalizeDecimal(input);
  if (n === null) return { error: 'Нужно число, например 8 682,03' };
  if (compareDecimal(n, '0') < 0) return { error: 'Ставка не может быть меньше нуля' };
  if (compareDecimal(n, RATE_MAX) > 0) return { error: 'Не больше 100 000 000 ₽' };
  if (fractionDigits(n) > 2) return { error: 'Не больше двух знаков после запятой' };
  return { value: n };
}

/** `model_ids` подсказки: отмечены все — без списка, это весь парк группы. */
export function suggestionModelIds(
  all: readonly string[],
  checked: ReadonlySet<string>,
): string[] | undefined {
  const picked = all.filter((id) => checked.has(id));
  return picked.length === all.length ? undefined : picked;
}

export interface RateInputsInfo {
  pick: GroupRatePick | null;
  modelIds: string[];
  modelsCount: number;
  versions: string[];
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function arrayOf(v: unknown): readonly unknown[] {
  return Array.isArray(v) ? v : [];
}

/**
 * Из чего получена ставка по подсказке (`inputs` записи): какое среднее, по
 * каким моделям и версиям ПРЦ. Чего нет — того нет: запись могла сохраниться
 * старым форматом.
 */
export function rateInputsInfo(inputs: unknown): RateInputsInfo | null {
  if (!isRecord(inputs)) return null;
  const models = arrayOf(inputs.models).filter(isRecord);
  const versions = new Set<string>();
  const modelIds: string[] = [];
  for (const m of models) {
    if (typeof m.model_id === 'string') modelIds.push(m.model_id);
    if (typeof m.version_label === 'string' && m.version_label.trim()) {
      versions.add(m.version_label.trim());
    }
  }
  const pick = inputs.pick === 'average' || inputs.pick === 'weighted' ? inputs.pick : null;
  return { pick, modelIds, modelsCount: models.length, versions: Array.from(versions) };
}

// ── ПРЦ модели ─────────────────────────────────────────────────────────────

export interface PrcTimeline {
  /** Цена, которая действует на дату. */
  current: FleetModelPrc | null;
  /** Утверждены, но ещё не вступили в силу — ближайшая первой. */
  upcoming: FleetModelPrc[];
  /** Прежние цены — свежие первыми. */
  past: FleetModelPrc[];
}

/**
 * Утверждённые ПРЦ модели на дату. «Последняя утверждённая» у backend —
 * без даты окончания, даже если её «действует с» ещё не наступило: тогда
 * сегодня действует прежняя цена.
 */
export function prcTimeline(history: readonly FleetModelPrc[], on: string): PrcTimeline {
  const sorted = [...history].sort((a, b) => b.valid_from.localeCompare(a.valid_from));
  const upcoming: FleetModelPrc[] = [];
  const past: FleetModelPrc[] = [];
  let current: FleetModelPrc | null = null;
  for (const p of sorted) {
    if (p.valid_from > on) upcoming.unshift(p);
    else if (!current && (p.valid_to === null || p.valid_to >= on)) current = p;
    else past.push(p);
  }
  return { current, upcoming, past };
}
